/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import styles from './TelemetryCardHeader.module.css';
import classnames from 'classnames';
import { Device, Unit, isLocationFullAccess, isLocationOwner, DeviceType, User, isEnabledDeveloperMenu } from 'model';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import "react-toggle/style.css";
import CopyToClipboard from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FloIconMenu } from 'components';
import { ConfirmationPopupFunction } from 'effects';

type TelemetryCardHeaderProps = {
  currentDevice:              Device,
  updatedTelemetryText:       string,
  currentUnit?:               Unit,
  loggedUser?:                User,
  deleteDevice:               (deviceId: string) => void,
  updateDevice:               (deviceId: string, device: Partial<Device>) => void,
  openTextConfirmationPopup:  ConfirmationPopupFunction,
  openConfirmationPopup:      ConfirmationPopupFunction,
  handleRenameDevicePopup:    ConfirmationPopupFunction,
  openSetThresholds:          () => void,
  openAutoShutoff:             () => void,
  openAlarmSettings:          () => void,
  runHealthTest:              (deviceId: string) => void,
  openDeveloperMenu:          () => void,
}

export const TelemetryCardHeader: React.FC<TelemetryCardHeaderProps> =  React.memo(({
  currentDevice,
  updatedTelemetryText,
  currentUnit,
  loggedUser,
  deleteDevice,
  updateDevice,
  openTextConfirmationPopup,
  openConfirmationPopup,
  handleRenameDevicePopup,
  openSetThresholds,
  openAutoShutoff,
  openAlarmSettings,
  runHealthTest,
  openDeveloperMenu,
}) => {
  const { t } = useTranslation();
  const [ isDeviceIdCopied, setDeviceIdAsCopied ] = useState(false);
  useEffect(() => setDeviceIdAsCopied(false), [currentDevice?.id]);

  const handleOnCopy = () => {
    setDeviceIdAsCopied(true);
    setTimeout(() => setDeviceIdAsCopied(false), 10000);
  };

  const handleUnpairDeviceClick = () => {
    openTextConfirmationPopup(
      t('unpair-device-title'),
      [t('unpair-device-description'), t('unpair-device-confirmation-description', { confirmationPhrase: currentDevice?.nickname || t('unpair-device-action-text')})],
      'Confirm',
      () => deleteDevice(currentDevice.id)
    );
  }

  const handleRenameDeviceClick = () => {
    handleRenameDevicePopup(
      t('rename-device-title'),
      t('rename-device-description'),
      'Confirm',
      (metadata) => updateDevice(currentDevice.id, { nickname: metadata?.input })
    );
  }

  const handleHealthTest = () => {
    openConfirmationPopup(
      t('health-test-confirm-title'),
      t('health-test-confirm-description'),
      t('health-test-confirm-action'),
      () => runHealthTest(currentDevice.id)
    );
  }

  return (
    <Row className={styles.header}>
      <Col xs="11" className={classnames(styles.smallerColPadding)}>
        <div className={styles.deviceName}>
          <span>{currentDevice?.nickname}</span>
          <OverlayTrigger
              key={'tooltip-copy-device-id'}
              placement={'auto'}
              overlay={
                <Tooltip id={'tooltip-copy-device-id'}>
                  <span className={styles.tooltipCopy}>{isDeviceIdCopied ? t('device-id-copied') : t('click-to-copy-device-id')}</span>
                </Tooltip>
              }
            >
            <CopyToClipboard text = {currentDevice.macAddress} onCopy={() => handleOnCopy()}>
              {isDeviceIdCopied ? <FontAwesomeIcon className={styles.copiedIcon} icon="clipboard-check" /> : <FontAwesomeIcon className={styles.copyIcon} icon="clipboard" /> }
            </CopyToClipboard>
          </OverlayTrigger>
        </div>
        <div>
          <span
            className={classnames(
              styles.deviceStatus,
              { [styles.online]: currentDevice.isConnected },
              { [styles.offline]: !currentDevice.isConnected }
            )}
          >{t(currentDevice.isConnected ? 'online' : 'offline')}</span>
          <span className={styles.lastUpdated}>{updatedTelemetryText}</span>
        </div>
      </Col>
      <Col xs="1" className={styles.smallerColPadding}>
        <FloIconMenu
          className = {styles.iconMenu}
          menuItems = {[
            { id: 'set-thresholds', title: t('Set Thresholds'), onClick: openSetThresholds, disabled: !isLocationFullAccess(currentUnit) },
            { id: 'auto-shutoff', title: t('Auto Shutoff'), onClick: openAutoShutoff, disabled: !isLocationFullAccess(currentUnit) },
            { id: 'manage-notifications', title: t('Manage Notifications'), onClick: openAlarmSettings },
            { id: 'run-health-test', title: t('Run Health Test'), onClick: handleHealthTest, disabled: !isLocationFullAccess(currentUnit) || currentDevice.type === DeviceType.DETECTOR },
            { id: 'rename-device', title: t('Rename Device'), onClick: () => handleRenameDeviceClick(), disabled: !isLocationOwner(currentUnit) },
            { id: 'unpair-device', title: t('Unpair Device'), onClick:  () => handleUnpairDeviceClick(), disabled: !isLocationOwner(currentUnit) },
            { id: 'developer-menu', title: t('Developer Menu'), onClick:  () => openDeveloperMenu(), disabled: !isLocationOwner(currentUnit) || currentDevice.type === DeviceType.DETECTOR || !isEnabledDeveloperMenu(loggedUser?.enabledFeatures || [""])},
          ]}
        />
      </Col>
    </Row>
  );
});
